import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { CreditCard, Tag, Loader2, CheckCircle, XCircle, Percent, Gift, ShoppingCart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Bouton de paiement Stripe réutilisable
 * Supporte les plans, packs de crédits et promotions
 * 
 * @param {string} type - "plan" | "credits"
 * @param {object} item - L'élément à acheter (plan ou pack)
 * @param {string} billingPeriod - "monthly" | "yearly" (pour les plans)
 * @param {function} getAuthHeader - Fonction pour obtenir le header d'auth
 * @param {function} onSuccess - Callback en cas de succès
 */
export default function StripeCheckoutButton({ 
  type = "credits",
  item,
  billingPeriod = "monthly",
  getAuthHeader,
  onSuccess: onSuccess,
  buttonText = "Acheter",
  buttonVariant = "default",
  className = ""
}) {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);

  // Calculer le prix de base
  const getBasePrice = () => {
    if (type === "plan") {
      return billingPeriod === "yearly" 
        ? (item.prix_annuel || item.prix_mensuel * 12)
        : item.prix_mensuel;
    }
    return item.prix_ttc || item.prix_ht || 0;
  };

  const basePrice = getBasePrice();
  const finalPrice = promoResult?.valid ? promoResult.final_amount : basePrice;
  const discount = promoResult?.valid ? promoResult.discount : 0;

  // Valider le code promo
  const handleValidatePromo = async () => {
    if (!promoCode.trim()) {
      toast.error('Veuillez entrer un code promo');
      return;
    }

    setValidatingPromo(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/stripe-subscriptions/validate-promo`,
        { promo_code: promoCode.toUpperCase(), amount: basePrice },
        { headers: getAuthHeader() }
      );
      
      setPromoResult(response.data);
      toast.success(`🎉 Code promo appliqué: -${response.data.discount}€`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Code promo invalide';
      toast.error(message);
      setPromoResult(null);
    }
    setValidatingPromo(false);
  };

  // Supprimer le code promo
  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoResult(null);
  };

  // Lancer le paiement
  const handleCheckout = async () => {
    setLoading(true);
    
    try {
      const endpoint = type === "plan" 
        ? `${BACKEND_URL}/api/stripe-subscriptions/checkout/plan`
        : `${BACKEND_URL}/api/stripe-subscriptions/checkout/credits`;
      
      const payload = type === "plan" 
        ? {
            plan_id: item.id,
            billing_period: billingPeriod,
            promo_code: promoResult?.valid ? promoCode : null,
            origin_url: window.location.origin
          }
        : {
            pack_id: item.id,
            promo_code: promoResult?.valid ? promoCode : null,
            origin_url: window.location.origin
          };
      
      const response = await axios.post(endpoint, payload, { headers: getAuthHeader() });
      
      if (response.data.checkout_url) {
        // Rediriger vers Stripe
        toast.success('Redirection vers le paiement...', { duration: 2000 });
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création du paiement';
      toast.error(message);
    }
    
    setLoading(false);
  };

  // Rendu du contenu du modal
  const renderItemDetails = () => {
    if (type === "plan") {
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-medium">{item.nom}</span>
            <Badge className="bg-blue-600 text-white font-semibold">{billingPeriod === "yearly" ? "Annuel" : "Mensuel"}</Badge>
          </div>
          <p className="text-sm text-gray-500">{item.description}</p>
          {item.fonctionnalites && (
            <ul className="text-sm space-y-1">
              {item.fonctionnalites.slice(0, 3).map((f, i) => (
                <li key={i} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  {f}
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }
    
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="font-medium">{item.nom}</span>
          <Badge className="bg-purple-600 text-white font-semibold">
            {item.credits_totaux || item.credits_inclus} crédits
          </Badge>
        </div>
        <p className="text-sm text-gray-500">{item.description}</p>
        {item.credits_bonus > 0 && (
          <Badge className="bg-green-600 text-white font-semibold">
            <Gift className="w-3 h-3 mr-1" />
            +{item.credits_bonus} bonus
          </Badge>
        )}
      </div>
    );
  };

  return (
    <>
      <Button 
        variant={buttonVariant}
        className={className}
        onClick={() => setShowModal(true)}
      >
        <ShoppingCart className="w-4 h-4 mr-2" />
        {buttonText}
      </Button>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-500" />
              Finaliser l'achat
            </DialogTitle>
            <DialogDescription>
              Vérifiez les détails et procédez au paiement sécurisé
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Détails de l'article */}
            <div className="bg-gray-50 p-4 rounded-lg">
              {renderItemDetails()}
            </div>

            {/* Code promo */}
            <div className="space-y-2">
              <Label>Code promo (optionnel)</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="PROMO2025"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  disabled={promoResult?.valid}
                  className="font-mono"
                />
                {promoResult?.valid ? (
                  <Button variant="outline" onClick={handleRemovePromo}>
                    <XCircle className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    onClick={handleValidatePromo}
                    disabled={validatingPromo || !promoCode.trim()}
                  >
                    {validatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
                  </Button>
                )}
              </div>
              {promoResult?.valid && (
                <div className="flex items-center gap-2 text-green-600 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  <span>Code "{promoResult.code}" appliqué: -{promoResult.discount}€</span>
                </div>
              )}
            </div>

            {/* Récapitulatif prix */}
            <div className="bg-blue-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span>Prix de base</span>
                <span>{basePrice.toFixed(2)}€</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span className="flex items-center gap-1">
                    <Percent className="w-3 h-3" /> Réduction
                  </span>
                  <span>-{discount.toFixed(2)}€</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total</span>
                <span className="text-blue-600">{finalPrice.toFixed(2)}€</span>
              </div>
            </div>

            {/* Sécurité */}
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <CreditCard className="w-4 h-4" />
              <span>Paiement sécurisé par Stripe. Vos données bancaires sont protégées.</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCheckout} 
              disabled={loading}
              className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Chargement...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Payer {finalPrice.toFixed(2)}€
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
